import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'fotbollsliv_session_token';

const AuthContext = createContext(null);

function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

function storeToken(token) {
  try { if (token) localStorage.setItem(TOKEN_KEY, token); } catch {}
}

function clearToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch {}
}

function authHeaders() {
  const token = getStoredToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/auth/me`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      } else {
        setUser(null);
        clearToken();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const safeFetch = async (url, options) => {
    const response = await fetch(url, options);
    let data = {};
    try {
      data = await response.json();
    } catch {
      // Response body may be unavailable - fall back to status-based handling
    }
    return { response, data };
  };

  const login = async (email, password) => {
    const { response, data } = await safeFetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });
    
    if (response.status === 429) {
      throw new Error(data.message || 'För många inloggningsförsök. Vänta en stund och försök igen.');
    }
    
    if (!response.ok) {
      throw new Error(data.detail || 'Fel e-post eller lösenord');
    }
    
    if (data.session_token) storeToken(data.session_token);
    setUser(data.user);
    return data;
  };

  const register = async (email, password, name, privacyConsent = true, marketingConsent = false) => {
    const { response, data } = await safeFetch(`${API}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name, privacy_consent: privacyConsent, marketing_consent: marketingConsent })
    });
    
    if (response.status === 429) {
      throw new Error(data.message || 'För många registreringsförsök. Vänta en stund och försök igen.');
    }
    
    if (response.status === 400) {
      throw new Error(data.detail || 'E-postadressen är redan registrerad');
    }
    
    if (!response.ok) {
      throw new Error(data.detail || 'Registrering misslyckades');
    }
    
    if (data.session_token) storeToken(data.session_token);
    setUser(data.user);
    return data;
  };

  const exchangeSession = async (sessionId) => {
    const { response, data } = await safeFetch(`${API}/api/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId })
    });

    if (response.ok && data.user) {
      if (data.session_token) storeToken(data.session_token);
      setUser(data.user);
    }
    return data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    clearToken();
    setUser(null);
  };

  const followTeam = async (teamId) => {
    const response = await fetch(`${API}/api/user/follow-team`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify({ team_id: teamId })
    });
    
    const data = await response.json();
    
    if (response.ok && user) {
      setUser({ ...user, followed_teams: data.followed_teams });
    }
    
    return data;
  };

  const unfollowTeam = async (teamId) => {
    const response = await fetch(`${API}/api/user/unfollow-team`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify({ team_id: teamId })
    });
    
    const data = await response.json();
    
    if (response.ok && user) {
      setUser({ ...user, followed_teams: data.followed_teams });
    }
    
    return data;
  };

  const updateNotificationSettings = async (settings) => {
    const response = await fetch(`${API}/api/user/notification-settings`, {
      method: 'PUT',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(settings)
    });
    
    const data = await response.json();
    
    if (response.ok && user) {
      setUser({ ...user, notification_settings: data.notification_settings });
    }
    
    return data;
  };

  const value = {
    user,
    loading,
    login,
    register,
    loginWithGoogle,
    exchangeSession,
    logout,
    followTeam,
    unfollowTeam,
    updateNotificationSettings,
    checkAuth,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
